/* eslint-disable react/require-default-props */

import React from 'react';
import { Box, Button, Icon, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { RuleImpact } from '../../../shared';

import Breadcrumbs from './Breadcrumbs/Breadcrumbs';
import { type ImpactLevel } from './RuleImpactIndicator';

import './RuleDetailsHeader.css';

interface RuleDetailsHeaderProps {
  /** main page title */
  title: string;
  /** Optional subtitle text */
  subtitle?: string;
  /** Icon to display next to the title */
  icon?: React.ReactNode;
  /** Whether to show breadcrumbs navigation */
  showBreadcrumbs?: boolean;
  /** impact level indicator */
  impactLevel?: ImpactLevel;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Header component for rule details page containing title, subtitle, breadcrumbs, and impact indicator
 *
 * @param props - The component props
 * @returns JSX element containing the rule details header
 */
function RuleDetailsHeader({
  title,
  subtitle,
  icon,
  showBreadcrumbs = false,
  impactLevel,
  className,
}: RuleDetailsHeaderProps) {
  return (
    <Box className={cx('Rules-RuleDetailsHeader', className)}>
      {showBreadcrumbs && (
        <Box className='Rules-RuleDetailsHeader-breadcrumbs'>
          <Breadcrumbs />
        </Box>
      )}

      <Box tag='header' className='Rules-RuleDetailsHeader-content'>
        <Box className='Rules-RuleDetailsHeader-topRow'>
          <Box className='Rules-RuleDetailsHeader-titleSection'>
            {icon && (
              <Box className='Rules-RuleDetailsHeader-icon'>
                {React.isValidElement(icon) && icon}
              </Box>
            )}

            <Box className='Rules-RuleDetailsHeader-titleRow'>
              <Typography
                kind='h1'
                color='--color-foreground-neutral-bold'
                className='Rules-RuleDetailsHeader-title'>
                {title}
              </Typography>
              {impactLevel && (
                <RuleImpact impactLevel={impactLevel} iconSize='small' />
              )}
            </Box>
          </Box>

          {/* Right side: Export button */}
          <Box className='Rules-RuleDetailsHeader-actions'>
            <Box className='Rules-RuleDetailsHeader-export'>
              <Button leftIcon={<Icon kind='Export' size='small' />}>
                Export
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Bottom: Subtitle aligned with left edge */}
        {subtitle && (
          <Typography
            kind='body2'
            color='--color-foreground-neutral-subtle'
            className='Rules-RuleDetailsHeader-subtitle'>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default RuleDetailsHeader;
